import { init, track } from "@plausible-analytics/tracker";

// Plausible Analytics configuration
const PLAUSIBLE_DOMAIN = "nis360.de";

/**
 * Initialize Plausible Analytics
 * Should be called once at app startup
 */
export const initPlausible = () => {
	init({
		domain: PLAUSIBLE_DOMAIN,
		captureOnLocalhost: true, // Enable tracking on localhost for development
	});
};

/**
 * Track a custom event with optional properties
 * Plausible only accepts string values for custom properties
 * @param eventName - Name of the event to track
 * @param props - Optional properties to attach to the event (will be converted to strings)
 */
export const trackEvent = (
	eventName: string,
	props?: Record<string, string | number | boolean>
) => {
	if (props) {
		// Convert all values to strings as Plausible only accepts string properties
		const stringProps: Record<string, string> = {};

		for (const [key, value] of Object.entries(props)) {
			stringProps[key] = String(value);
		}

		track(eventName, { props: stringProps });
	} else {
		track(eventName, { props: {} });
	}
};

// Questionnaire tracking events
export const QuestionnaireEvents = {
	// Intro events
	INTRO_START: "questionnaire_intro_start",
	INTRO_CONTINUE: "questionnaire_intro_continue",

	// Business Info events (Betroffenheitsanalyse)
	BUSINESS_STEP: "questionnaire_business_step",
	BUSINESS_COMPLETE: "questionnaire_business_complete",

	// Needs Analysis events (Bedarfsanalyse)
	NEEDS_START: "questionnaire_needs_start",
	NEEDS_STEP: "questionnaire_needs_step",
	NEEDS_COMPLETE: "questionnaire_needs_complete",

	// GAP Analysis events (Self Assessment)
	GAP_START: "questionnaire_gap_start",
	GAP_STEP: "questionnaire_gap_step",
	GAP_COMPLETE: "questionnaire_gap_complete",
} as const;

// Marketing-site funnel events (website redesign: product-first, quick check as lead magnet)
// Funnel chain: check CTA click → check started/completed (QuestionnaireEvents) →
// registration click → demo/contact requests
export const FunnelEvents = {
	CHECK_CTA_CLICK: "funnel_check_cta_click",
	RESULT_REGISTER_CLICK: "funnel_result_register_click",
	DIRECT_REGISTER_CLICK: "funnel_direct_register_click",
	DEMO_REQUEST_CLICK: "funnel_demo_request_click",
	PRICING_PLAN_CLICK: "funnel_pricing_plan_click",
} as const;

/**
 * Track a click on a "Schnellcheck starten" CTA.
 * @param source - where the CTA was clicked (e.g. "header", "hero", "how_it_works",
 *                 "lead_magnet", "product_page", "pricing_page", "quickcheck_page")
 */
export const trackCheckCtaClick = (source: string) => {
	trackEvent(FunnelEvents.CHECK_CTA_CLICK, { source });
};

/** Track a click on "register" from the questionnaire result page */
export const trackResultRegisterClick = (result: string) => {
	trackEvent(FunnelEvents.RESULT_REGISTER_CLICK, { result });
};

/** Track a direct registration click that did not come from the check result */
export const trackDirectRegisterClick = (source: string) => {
	trackEvent(FunnelEvents.DIRECT_REGISTER_CLICK, { source });
};

/** Track a click on "Demo vereinbaren" / contact-for-demo CTAs */
export const trackDemoRequestClick = (source: string) => {
	trackEvent(FunnelEvents.DEMO_REQUEST_CLICK, { source });
};

/** Track a click on a pricing plan CTA */
export const trackPricingPlanClick = (plan: string) => {
	trackEvent(FunnelEvents.PRICING_PLAN_CLICK, { plan });
};

// Helper functions for specific tracking scenarios
export const trackQuestionnaireIntroStart = () => {
	trackEvent(QuestionnaireEvents.INTRO_START);
};

export const trackQuestionnaireIntroContinue = () => {
	trackEvent(QuestionnaireEvents.INTRO_CONTINUE);
};

export const trackBusinessStep = (step: number, stepName: string) => {
	trackEvent(QuestionnaireEvents.BUSINESS_STEP, {
		step,
		stepName,
	});
};

export const trackBusinessComplete = () => {
	trackEvent(QuestionnaireEvents.BUSINESS_COMPLETE);
};

export const trackNeedsStart = (totalSteps: number) => {
	trackEvent(QuestionnaireEvents.NEEDS_START, { totalSteps });
};

export const trackNeedsStep = (step: number, totalSteps: number) => {
	trackEvent(QuestionnaireEvents.NEEDS_STEP, { step, totalSteps });
};

export const trackNeedsComplete = (result: string) => {
	trackEvent(QuestionnaireEvents.NEEDS_COMPLETE, { result });
};

export const trackGapStart = (totalSteps: number) => {
	trackEvent(QuestionnaireEvents.GAP_START, { totalSteps });
};

export const trackGapStep = (step: number, substep: number, stepName: string) => {
	trackEvent(QuestionnaireEvents.GAP_STEP, { step, substep, stepName });
};

export const trackGapComplete = () => {
	trackEvent(QuestionnaireEvents.GAP_COMPLETE);
};
